import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { produce } from 'immer';
import { queryItemList } from './api';
import { useCallback, useMemo } from 'react';
import { ItemIdMap as ItemIdMap2 } from './stateForSellerSku';
import type { KeywordType } from './api';

type QueryParamsType = Parameters<typeof queryItemList>[0];

const defaultQueryParams: () => QueryParamsType = () => {
  return {
    pageNo: 1,
    pageSize: 10,
    platformStoreScope: [],
    tagId: [],
    keywordType: 1,
    keyword: '',
    storeType: 0,
  };
};

/***************** 基础状态 start ******************/
export const QueryParams = atom<QueryParamsType>(defaultQueryParams());

export const useQueryParams = () => {
  return useAtomValue(QueryParams);
};

export const useLabelQuery = () => {
  return useAtomValue(QueryParams).tagId;
};

export const CurrentPageExternalItemIdsState = atom<string[]>([]);

export const ItemIdMap = atom<Record<string, ItemSelectV2.Item>>({});

export const CurrentItems = atom<ItemSelectV2.Item[]>((get) => {
  const itemIds = get(CurrentPageExternalItemIdsState);
  const itemIdMap = get(ItemIdMap);

  return itemIds.map((id) => itemIdMap[id]).filter(Boolean);
});

export const SelectedItems = atom<ItemSelectV2.Item[]>((get) => {
  const itemIdMap = get(ItemIdMap);
  return Object.values(itemIdMap).filter(
    (item) => item._checked || item.skuInfo.some((sku) => sku._checked),
  );
});

export const useSelectedItems = () => {
  return useAtomValue(SelectedItems);
};

export const CurrentPageCheckboxState = atom<'checked' | 'indeterminate' | 'unchecked'>((get) => {
  const itemIds = get(CurrentPageExternalItemIdsState);
  const itemIdMap = get(ItemIdMap);

  const checkedCount = itemIds.filter((id) => itemIdMap[id]?._checked).length;
  const indeterminateCount = itemIds.filter((id) => itemIdMap[id]?._indeterminate).length;
  const totalCount = itemIds.length;

  if (totalCount === 0) {
    return 'unchecked';
  } else if (checkedCount === totalCount && indeterminateCount === 0) {
    return 'checked';
  } else if (checkedCount === 0) {
    return 'unchecked';
  } else {
    return 'indeterminate';
  }
});

export const useCurrentPageCheckboxState = () => {
  return useAtomValue(CurrentPageCheckboxState);
};

/***************** 基础状态 end ********************/

/************** 更改状态的方法 start ****************/
// 分页
export function usePage() {
  const [state, setstate] = useAtom(QueryParams);
  const change = useCallback(
    (pageNo: number, pageSize?: number) => {
      setstate(
        produce((draft) => {
          draft.pageNo = pageNo;
          if (pageSize && pageSize !== draft.pageSize) {
            draft.pageSize = pageSize;
            draft.pageNo = 1;
          }
        }),
      );
    },
    [setstate],
  );
  return [state.pageNo, state.pageSize, change] as const;
}

// 修改storeScope
export function useStoreScope() {
  const [state, setState] = useAtom(QueryParams);
  const change = useCallback(
    (storeScopes: General.StoreScope[]) => {
      setState(
        produce((draft) => {
          draft.platformStoreScope = storeScopes;
          draft.pageNo = 1;
        }),
      );
    },
    [setState],
  );

  return [state.platformStoreScope, change] as const;
}

// 修改搜索标签
export function useSearchTag() {
  const [state, setState] = useAtom(QueryParams);
  const change = useCallback(
    (tagIds: string[]) => {
      setState(
        produce((draft) => {
          draft.tagId = tagIds;
          draft.pageNo = 1;
        }),
      );
    },
    [setState],
  );

  return [state.tagId, change] as const;
}

// 修改关键字
export function useKeyword() {
  const [state, setState] = useAtom(QueryParams);
  const changeKeywordBoth = useCallback(
    (keywordType: KeywordType, keyword: string) => {
      setState(
        produce((draft) => {
          draft.keywordType = keywordType;
          draft.keyword = keyword;
          draft.pageNo = 1;
        }),
      );
    },
    [setState],
  );

  const changeKeyword = useCallback(
    (keyword: string) => {
      setState(
        produce((draft) => {
          draft.keyword = keyword;
          draft.pageNo = 1;
        }),
      );
    },
    [setState],
  );

  return {
    keywordType: state.keywordType,
    setKeywordBoth: changeKeywordBoth,
    keyword: state.keyword,
    setKeyword: changeKeyword,
  } as const;
}

// 修改sku选中状态
// 公用方法
export const useToggleSkuChecked = (mode: 'item' | 'sellerSku') => {
  const atom = useMemo(() => (mode === 'item' ? ItemIdMap : ItemIdMap2), [mode]);
  const setItemIdMap = useSetAtom(atom);

  return useCallback(
    (checked: boolean, itemId: string, skuId: string) => {
      setItemIdMap(
        produce((draft) => {
          const item = draft[itemId];
          if (mode === 'item') {
            const sku = item.skuInfo.find((sku) => sku.skuId === skuId);
            if (sku) {
              sku._checked = checked;
            }
            const checkedSkuCount = item.skuInfo.filter((sku) => sku._checked).length;
            if (checkedSkuCount === item.skuInfo.length) {
              item._indeterminate = false;
              item._checked = true;
            } else if (checkedSkuCount === 0) {
              item._indeterminate = false;
              item._checked = false;
            } else {
              item._indeterminate = true;
              item._checked = false;
            }
          } else {
            const sku = item.skuInfo.find((sku) => sku.skuId === skuId);
            if (sku) {
              sku._checked = checked;
              item._checked = checked;
              item._indeterminate = false;
            }
          }
        }),
      );
    },
    [setItemIdMap, mode],
  );
};

// 修改spu选中状态
// 公用方法
export const useToggleSpuChecked = (mode: 'item' | 'sellerSku', sellerSku?: string) => {
  const atom = useMemo(() => (mode === 'item' ? ItemIdMap : ItemIdMap2), [mode]);
  const setItemIdMap = useSetAtom(atom);

  return useCallback(
    (checked: boolean, itemId: string) => {
      setItemIdMap(
        produce((draft) => {
          const item = draft[itemId];
          if (mode === 'item') {
            item._checked = checked;
            item._indeterminate = false;
            item.skuInfo.forEach((sku) => {
              sku._checked = item._checked;
            });
          } else {
            item._checked = checked;
            item._indeterminate = false;
            item.skuInfo
              .filter((sku) => sku.sellerSku.toLowerCase() === sellerSku?.toLowerCase())
              .forEach((sku) => {
                sku._checked = item._checked;
              });
          }
        }),
      );
    },
    [setItemIdMap, sellerSku, mode],
  );
};

// 修改本页全选状态
export const useToggleCurrentPageChecked = () => {
  const setItemIdMap = useSetAtom(ItemIdMap);
  const currentPageExternalItemIds = useAtomValue(CurrentPageExternalItemIdsState);

  return useCallback(
    (checked: boolean) => {
      setItemIdMap(
        produce((draft) => {
          currentPageExternalItemIds.forEach((itemId) => {
            const item = draft[itemId];
            item._checked = checked;
            item._indeterminate = false;
            item.skuInfo.forEach((sku) => {
              sku._checked = checked;
            });
          });
        }),
      );
    },
    [setItemIdMap, currentPageExternalItemIds],
  );
};

// 清空所有商品
export const useCleanAllSpu = () => {
  const setItemIdMap = useSetAtom(ItemIdMap);
  return useCallback(() => {
    setItemIdMap(
      produce((draft) => {
        Object.values(draft).forEach((item) => {
          item._checked = false;
          item._indeterminate = false;
          item.skuInfo.forEach((sku) => {
            sku._checked = false;
          });
        });
      }),
    );
  }, [setItemIdMap]);
};

export const useClearAll = () => {
  const setItemIdMap = useSetAtom(ItemIdMap);
  const setIds = useSetAtom(CurrentPageExternalItemIdsState);
  const setParams = useSetAtom(QueryParams);

  return useCallback(() => {
    setIds([]);
    setItemIdMap({});
    setParams(defaultQueryParams());
  }, [setParams, setIds, setItemIdMap]);
};
/*************** 更改状态的方法 end *****************/
